import 'react-quill/dist/quill.snow.css';

import { type CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import showdown from 'showdown';
import styled from 'styled-components';

const converter = new showdown.Converter();

type Props = ReactQuill.ReactQuillProps & {
  textStyleCss?: StyledCss;
  wrapperStyle?: CSSProperties;
  onChange?: (value: string) => void;
};

const SimpleEditor = (props: Props) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<ReactQuill>(null);
  const [load, setLoad] = useState(false);

  const modules = useMemo(() => {
    return {
      toolbar: [
        [{ header: 1 }, { header: 2 }, { header: 3 }, 'clean'],
        ['bold', 'italic', 'underline', 'strike'],
      ],
      clipboard: {
        matchVisual: false,
      },
    };
  }, []);

  useEffect(() => {
    const loadQuill = async () => {
      const imported = await import('react-quill');
      const Quill = imported.Quill;
      const icons = Quill.import('ui/icons');
      icons.header[1] = '<span style="font-weight: 500;">H1</span>';
      icons.header[2] = '<span style="font-weight: 500;">H2</span>';
      icons.header[3] = '<span style="font-weight: 500;">H3</span>';
      icons['clean'] = '<span style="font-weight: 500;">Description</span>';
      setLoad(true);
    };

    loadQuill();
  }, []);

  return (
    <Style
      ref={parentRef}
      style={props.wrapperStyle}
      textStyleCss={props.textStyleCss}
      onPasteCapture={(e) => {
        const editor = quillRef.current?.getEditor();
        if (!editor) return;

        const text = e.clipboardData.getData('text/plain');
        if (!text) return;

        e.preventDefault();
        e.stopPropagation();

        const html = converter.makeHtml(text);
        const selection = editor.getSelection();
        // 커서 위치가 없으면 에디터 끝에 삽입
        const index = selection ? selection.index : editor.getLength() - 1;

        // 선택된 텍스트가 있으면 삭제 후 삽입
        if (selection && selection.length > 0) {
          editor.deleteText(index, selection.length);
        }
        editor.clipboard.dangerouslyPasteHTML(index, html);
      }}
    >
      {load && (
        <ReactQuill {...props} ref={quillRef} modules={modules} theme="snow" />
      )}
    </Style>
  );
};

type StyleProps = {
  textStyleCss?: StyledCss;
};

const Style = styled.div<StyleProps>`
  position: relative;
  width: 100%;
  overflow: visible;
  display: flex;
  flex-direction: column;
  height: 100%;

  ul,
  ol {
    padding-left: 12px;
  }

  li {
    margin-bottom: 4px;
  }

  &&&& .ql-tooltip {
    z-index: 1000;
    width: fit-content;
  }
  &&&& .ql-editor {
    ${(props) => props.textStyleCss};
    max-height: 100%;
    overflow-y: auto;
  }
  &&&& .ql-toolbar {
    position: sticky;
    top: 0;
    background: ${(props) => props.theme.colors.gray200};
    z-index: 10;
    border: none;
    border-bottom: 1px solid ${(props) => props.theme.colors.gray400};
    .ql-clean {
      width: 90px;
    }
  }
  &&&& .ql-container.ql-snow {
    border: none;
    flex: 1;
    overflow: hidden;
  }
  &&&& .ql-snow.ql-toolbar button {
    color: ${(props) => props.theme.colors.gray700};
  }
  &&&& .ql-snow .ql-fill {
    fill: ${(props) => props.theme.colors.gray700};
  }
  &&&& .ql-snow .ql-stroke {
    stroke: ${(props) => props.theme.colors.gray700};
  }
`;

export default SimpleEditor;
